import type { FollowUpOutcomeKind, FollowUpView } from '@sigora/domain';
import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  type BriefReadView,
  askFollowUp,
  chooseFollowUpOutcome,
  fetchBrief,
  retryFollowUp,
} from '../../api/client.ts';
import { domainCodeOf, followUpMoved, isApiError } from '../../api/errors.ts';
import { ChatPanel, type DraftStore } from '../../chat/ChatPanel.tsx';
import { CHAT_GREETING } from '../../chat/chat-copy.ts';
import type { SigoraUIMessage } from '../../chat/events.ts';
import {
  FOLLOW_UP_ACTION_FAILED,
  FOLLOW_UP_ANSWERING,
  FOLLOW_UP_ANSWER_RUNNING,
  FOLLOW_UP_CARD_GONE,
  FOLLOW_UP_LOAD_FAILED,
  FOLLOW_UP_NOT_SENT,
  FOLLOW_UP_RETRY_WAITS,
  FOLLOW_UP_SEND_AGAIN_LABEL,
  FOLLOW_UP_SEND_FAILED,
  FOLLOW_UP_SCOPE_REMOVE_LABEL,
  FOLLOW_UP_SEND_REFUSED,
  FOLLOW_UP_WAITING,
  followUpScopeLine,
} from '../brief-copy.ts';
import type { ComposerOffer, StepThreadHandle } from '../StepThread.tsx';
import { FollowUpAnswer, noteTargetOf } from './FollowUpAnswer.tsx';
import type { NoteCard } from './NoteCardPicker.tsx';
import { useFollowUps } from './use-follow-ups.ts';

/**
 * A follow-up's id, minted by this tab per question and sent again with it on a retry, so a send
 * whose answer was lost is answered with the follow-up already stored. `crypto.randomUUID` is not
 * defined outside a secure context — an ip-address origin on a phone is the ordinary way to look
 * at this on a device — so the same version-4 uuid is built from random bytes there.
 */
export const mintFollowUpId = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** The card a question is about, from Ask about this: its research question's id and words. */
export interface FollowUpScope {
  rqId: string;
  label: string;
}

/** A question whose send failed, with the id it went under and the card it was asked about. */
export interface UnsentQuestion {
  id: string;
  question: string;
  rqId: string | null;
}

/**
 * What the Brief's composer keeps beside its words past the thread's own mount: the questions that
 * were not taken, the id and card each question whose send failed went under, by its words, and
 * the card the next question is about.
 */
export interface HeldFollowUps {
  notTaken: readonly UnsentQuestion[];
  /** By `sentKey`: the same words about another card are another question. */
  sentUnder: ReadonlyMap<string, { id: string; rqId: string | null }>;
  scope: FollowUpScope | null;
}

/**
 * Where `HeldFollowUps` is kept: by the study's shell, beside the composer's words, so a question
 * whose send failed and its id outlive a walk to another step. A send that answers after the
 * thread was left writes here, and the thread drawn again reads it; a write tells whichever thread
 * is drawn, so an offer taken by a later mount clears what an earlier mount kept.
 */
export interface FollowUpMemory {
  read: () => HeldFollowUps;
  write: (held: HeldFollowUps) => void;
  listen: (listener: () => void) => () => void;
}

/** A question's words and the card it is about, as one key. */
const sentKey = (question: string, rqId: string | null): string => JSON.stringify([question, rqId]);

const NOTHING_HELD: HeldFollowUps = { notTaken: [], sentUnder: new Map(), scope: null };

export const createFollowUpMemory = (): FollowUpMemory => {
  let held = NOTHING_HELD;
  const listeners = new Set<() => void>();

  return {
    read: () => held,
    write: (next) => {
      held = next;
      for (const listener of listeners) {
        listener();
      }
    },
    listen: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export interface FollowUpThreadProps {
  ref?: Ref<StepThreadHandle>;
  studyId: string;
  draftStore: DraftStore;
  restore: ComposerOffer | null;
  /**
   * Hands words back to the Brief's composer: a question that was not taken. `onTaken` is told once
   * the composer holds them, which it declines to while it holds newer words.
   */
  offer: (
    step: 'brief',
    text: string,
    onTaken?: () => void,
    options?: { reveal?: boolean; append?: boolean },
  ) => void;
  /**
   * A follow-up was finalized — a note or a log line, or an update of the Brief written — so the
   * Brief on the page moves with it: given the Brief a choice made here was answered with, or
   * `null` when a read found it, for the Brief to be read again.
   */
  onFinalized: (read: BriefReadView | null) => void;
  /** Where what the composer keeps beside its words outlives this mount; the thread's own if none. */
  memory?: FollowUpMemory;
  /**
   * Whether the Ask Sigora drawer is open, and the way to open or close it. Held by the shell, so
   * the drawer stays open as the researcher walks from one step's thread to the next.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

/** A Brief with no cards on hand: every label falls back to none, and a note asks for none. */
const NO_CARDS: readonly NoteCard[] = [];

/**
 * The Brief step's thread, where every question asked is a follow-up and nothing stays only in
 * chat. A question is stored and answered by the worker, never by a conversation turn; the thread
 * reads its follow-ups from their own route, draws each question as the researcher's message and
 * its answer where a reply would be, and under the answer offers the three ways it can land. Each
 * follow-up is held on the server, so closing the tab, or reloading it, finds a choice still waiting.
 *
 * An offer carrying a card — Ask about this, on the Brief or on a past version of it — makes the
 * next question about that card: the composer shows it as a removable "About" line, and the
 * question is sent with the card's research question, so its answer keeps to that card's evidence.
 * The line goes once a question about it is stored, or when the researcher removes it.
 */
export const FollowUpThread = ({
  ref,
  studyId,
  draftStore,
  restore,
  offer,
  onFinalized,
  memory,
  expanded,
  onExpandedChange,
}: FollowUpThreadProps) => {
  const follow = useFollowUps(studyId, onFinalized);
  const [ownMemory] = useState(createFollowUpMemory);
  const store = memory ?? ownMemory;
  /**
   * What the composer keeps beside its words (`HeldFollowUps`):
   *
   * - `notTaken`: questions that were not taken, each drawn as the researcher's message with Send
   *   again under it until it is sent again or the composer takes its words back. A composer already
   *   holding newer words declines them, and this is then where they still are.
   * - `sentUnder`: the id and card each question not yet stored went under, by its words — held
   *   from the moment it is sent until the server answers that it is stored. Sent again — by Send
   *   again or from the composer, about the same card, and from a thread drawn again while the
   *   first send is still on its way — it goes under the same id, so a first send that did reach
   *   the server, whatever was lost on the way back, is answered rather than asked twice. The same
   *   words about another card are another question.
   * - `scope`: the card the next question is about, or null for the whole study.
   */
  const { notTaken, scope } = useSyncExternalStore(store.listen, store.read);
  const keep = useCallback(
    (change: Partial<HeldFollowUps>) => store.write({ ...store.read(), ...change }),
    [store],
  );
  /** The last offer whose card was taken up, so each offer sets the card once. */
  const scopedOffer = useRef<number | null>(null);
  useEffect(() => {
    if (restore?.scope === undefined || restore.token === scopedOffer.current) {
      return;
    }
    scopedOffer.current = restore.token;
    keep({ scope: restore.scope });
  }, [restore, keep]);
  /** The question on its way to the server, drawn as sent until it is stored. */
  const [sending, setSending] = useState<{ id: string; question: string } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  /** The follow-ups a choice or a Try again is on its way for, readable at once against a double press. */
  const pressing = useRef(new Set<string>());
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set());
  const [actionErrors, setActionErrors] = useState<Readonly<Record<string, string>>>({});
  const [pickingFor, setPickingFor] = useState<string | null>(null);
  /** The follow-up this tab just finalized, whose outcome line takes focus from the buttons it replaced. */
  const [finalizedHere, setFinalizedHere] = useState<string | null>(null);
  /** The Brief's cards: `undefined` until read, `null` when the read failed. */
  const [cards, setCards] = useState<readonly NoteCard[] | null | undefined>(undefined);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const followUps = follow.followUps;
  const answering = followUps?.some((followUp) => followUp.state === 'answering') ?? false;
  const waiting = followUps?.some((followUp) => followUp.state === 'choosing') ?? false;

  /**
   * Whether anything on screen names a card: an answer that relates to one, a note under one, or a
   * note waiting for one to be picked. A thread with none never reads the Brief for its cards.
   */
  const namesCards =
    pickingFor !== null ||
    (followUps?.some(
      (followUp) =>
        followUp.answer !== null &&
        (noteTargetOf(followUp) !== null || followUp.outcome?.kind === 'note'),
    ) ??
      false);

  /**
   * The Brief's cards, for naming the card an answer relates to and for placing a note, read once
   * something names one. Cards are one per accepted research question and never change between
   * versions, so one read serves.
   */
  useEffect(() => {
    if (!namesCards || cards !== undefined) {
      return;
    }

    const controller = new AbortController();
    fetchBrief(studyId, controller.signal)
      .then((read) => {
        if (!controller.signal.aborted) {
          setCards(
            read.brief?.content.evidence.cards.map((card) => ({
              rqId: card.rq_id,
              text: card.rq_text,
            })) ?? NO_CARDS,
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCards(null);
        }
      });

    return () => {
      controller.abort();
    };
  }, [studyId, namesCards, cards]);

  const cardText = useCallback(
    (rqId: string) => cards?.find((card) => card.rqId === rqId)?.text ?? null,
    [cards],
  );

  useImperativeHandle(
    ref,
    () => ({
      // Nothing is approved on the Brief.
      adoptApproval: () => undefined,
      // A question on the Brief is only ever sent by the researcher: words asked to be sent go
      // into the composer, after any already there.
      ask: (text, { send }) => {
        offer('brief', text, undefined, { reveal: true, ...(send ? { append: true } : {}) });
      },
    }),
    [offer],
  );

  const press = (followUpId: string, pressed: boolean) => {
    if (pressed) {
      pressing.current.add(followUpId);
    } else {
      pressing.current.delete(followUpId);
    }
    setBusyIds(new Set(pressing.current));
  };

  const sayFor = (followUpId: string, error: string | null) =>
    setActionErrors((current) => {
      if (error === null) {
        const { [followUpId]: _cleared, ...rest } = current;
        return rest;
      }

      return { ...current, [followUpId]: error };
    });

  /**
   * What a refused write does: a thread out of date is read again, and anything else is said. Asked
   * again while another question is being answered — from another tab, since this one holds Try
   * again until it is — it says why as well.
   */
  const refused = (followUpId: string, error: unknown) => {
    if (domainCodeOf(error) === 'follow_up_answer_running') {
      sayFor(followUpId, FOLLOW_UP_RETRY_WAITS);
      void follow.reload();
      return;
    }
    if (followUpMoved(error)) {
      void follow.reload();
      return;
    }

    sayFor(followUpId, FOLLOW_UP_ACTION_FAILED);
  };

  /**
   * Sends a question, about the card it is given or else the one on the composer, under the id it
   * is given, else the one its words failed under about that card, else a new one.
   */
  const send = (text: string, sentBefore?: UnsentQuestion) => {
    const question = text.trim();
    // The card on the composer when it was sent from there: only that card, and not one a later
    // Ask about this put there meanwhile, is taken off once the question is stored.
    const sentScope = sentBefore === undefined ? scope : null;
    const rqId = sentBefore === undefined ? (scope?.rqId ?? null) : sentBefore.rqId;
    const key = sentKey(question, rqId);
    const id = sentBefore?.id ?? store.read().sentUnder.get(key)?.id ?? mintFollowUpId();
    const others = (kept: HeldFollowUps) =>
      kept.notTaken.filter((each) => sentKey(each.question, each.rqId) !== key);
    setSendError(null);
    const held = store.read();
    keep({
      notTaken: others(held),
      sentUnder: new Map(held.sentUnder).set(key, { id, rqId }),
    });
    setSending({ id, question });

    // What a send keeps is written whether or not the thread is still drawn, so a send that
    // answers after the researcher left the step is found when they come back.
    askFollowUp({ studyId, id, question, rqId })
      .then(({ followUp }) => {
        const kept = store.read();
        const sentUnder = new Map(kept.sentUnder);
        sentUnder.delete(key);
        // The card is asked about; a question after it is about the whole study unless asked.
        keep({
          sentUnder,
          ...(sentScope !== null && kept.scope === sentScope ? { scope: null } : {}),
        });
        if (alive.current) {
          follow.adopt(followUp);
        }
      })
      .catch((error: unknown) => {
        const kept = store.read();
        const cardGone = domainCodeOf(error) === 'follow_up_unknown_card';
        // The question as it now waits: about the whole study once its card is gone.
        const waitsAs = cardGone ? sentKey(question, null) : key;
        if (cardGone) {
          // Nothing was stored, and the card cannot be asked about: it goes from the composer, and
          // the question waits to be asked about the whole study.
          const sentUnder = new Map(kept.sentUnder);
          sentUnder.delete(key);
          keep({
            sentUnder,
            notTaken: [...others(kept), { id: mintFollowUpId(), question, rqId: null }],
            ...(kept.scope?.rqId === rqId ? { scope: null } : {}),
          });
        } else {
          keep({
            sentUnder: new Map(kept.sentUnder).set(key, { id, rqId }),
            notTaken: [...others(kept), { id, question, rqId }],
          });
        }
        // The words go back to the composer whatever happened, and stay on screen until it holds
        // them, so nothing typed is lost.
        offer('brief', question, () => {
          keep({
            notTaken: store
              .read()
              .notTaken.filter((each) => sentKey(each.question, each.rqId) !== waitsAs),
          });
        });
        if (!alive.current) {
          return;
        }

        if (!isApiError(error)) {
          setSendError(FOLLOW_UP_SEND_FAILED);
          return;
        }

        const code = domainCodeOf(error);
        setSendError(
          code === 'follow_up_answer_running'
            ? FOLLOW_UP_ANSWER_RUNNING
            : code === 'follow_up_unknown_card'
              ? FOLLOW_UP_CARD_GONE
              : FOLLOW_UP_SEND_REFUSED,
        );
        if (followUpMoved(error)) {
          void follow.reload();
        }
      })
      .finally(() => {
        if (alive.current) {
          setSending(null);
        }
      });
  };

  const choose = (followUp: FollowUpView, kind: FollowUpOutcomeKind, rqId: string | null) => {
    if (pressing.current.has(followUp.id)) {
      return;
    }
    // A note needs a card, and only one nothing names is asked for.
    if (kind === 'note' && rqId === null && noteTargetOf(followUp) === null) {
      setPickingFor(followUp.id);
      return;
    }

    press(followUp.id, true);
    sayFor(followUp.id, null);
    chooseFollowUpOutcome({ studyId, followUpId: followUp.id, kind, rqId })
      .then(({ followUp: chosen, read }) => {
        if (!alive.current) {
          return;
        }
        setPickingFor((current) => (current === followUp.id ? null : current));
        if (chosen.state === 'finalized') {
          setFinalizedHere(chosen.id);
        }
        follow.adopt(chosen, read);
        if (chosen.state === 'updating') {
          follow.waitAgain(chosen.id);
        }
      })
      .catch((error: unknown) => {
        if (!alive.current) {
          return;
        }
        if (domainCodeOf(error) === 'follow_up_note_needs_card') {
          setPickingFor(followUp.id);
          return;
        }
        refused(followUp.id, error);
      })
      .finally(() => {
        if (alive.current) {
          press(followUp.id, false);
        }
      });
  };

  const retry = (followUp: FollowUpView) => {
    if (pressing.current.has(followUp.id)) {
      return;
    }

    press(followUp.id, true);
    sayFor(followUp.id, null);
    retryFollowUp(studyId, followUp.id)
      .then((asked) => {
        if (alive.current) {
          follow.adopt(asked);
          follow.waitAgain(asked.id);
        }
      })
      .catch((error: unknown) => {
        if (alive.current) {
          refused(followUp.id, error);
        }
      })
      .finally(() => {
        if (alive.current) {
          press(followUp.id, false);
        }
      });
  };

  const checkAgain = (followUpId: string) => {
    follow.waitAgain(followUpId);
    void follow.reload();
  };

  /**
   * The conversation as the panel draws it: Sigora's greeting on a thread with nothing asked, then
   * each question as the researcher's message and its follow-up where a reply would be, then the
   * questions that were not taken, then a question still on its way.
   */
  const messages = useMemo<SigoraUIMessage[]>(() => {
    const held = followUps ?? [];
    const asked: SigoraUIMessage[] = held.flatMap((followUp) => [
      {
        id: `${followUp.id}-question`,
        role: 'user',
        parts: [{ type: 'text', text: followUp.question }],
      },
      {
        id: `${followUp.id}-answer`,
        role: 'assistant',
        parts: [{ type: 'data-follow-up', id: followUp.id, data: followUp }],
      },
    ]);
    const unanswered: SigoraUIMessage[] = notTaken
      .filter((question) => !held.some((followUp) => followUp.id === question.id))
      .map((question) => ({
        id: notTakenMessageId(question.id),
        role: 'user',
        parts: [{ type: 'text', text: question.question }],
      }));
    const pending: SigoraUIMessage[] =
      sending === null || held.some((followUp) => followUp.id === sending.id)
        ? []
        : [
            {
              id: `${sending.id}-question`,
              role: 'user',
              parts: [{ type: 'text', text: sending.question }],
            },
          ];
    const opening: SigoraUIMessage[] =
      followUps !== null && held.length === 0 && unanswered.length === 0 && pending.length === 0
        ? [
            {
              id: `greeting-${studyId}-brief`,
              role: 'assistant',
              parts: [{ type: 'text', text: CHAT_GREETING }],
            },
          ]
        : [];

    return [...opening, ...asked, ...unanswered, ...pending];
  }, [followUps, notTaken, sending, studyId]);

  return (
    <ChatPanel
      collapsible
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      messages={messages}
      status={sending !== null ? 'submitted' : answering ? 'streaming' : 'ready'}
      activity={null}
      error={sendError === null ? undefined : new Error(sendError)}
      notice={null}
      label="Ask Sigora about this brief"
      draftStore={draftStore}
      restore={restore}
      historyLoading={followUps === null && !follow.loadFailed}
      historyError={follow.loadFailed ? FOLLOW_UP_LOAD_FAILED : null}
      cancelledIds={NO_CANCELLED}
      onSend={(text) => send(text)}
      composerNote={
        scope === null ? null : (
          <p className="follow-up-scope" data-test-id="follow-up-scope">
            <span data-test-id="follow-up-scope-label">{followUpScopeLine(scope.label)}</span>
            <button
              type="button"
              className="text-link"
              data-test-id="follow-up-scope-remove"
              onClick={() => keep({ scope: null })}
            >
              {FOLLOW_UP_SCOPE_REMOVE_LABEL}
            </button>
          </p>
        )
      }
      closedNote={answering ? FOLLOW_UP_ANSWERING : waiting ? FOLLOW_UP_WAITING : null}
      renderUserNote={(messageId) => {
        const question = notTaken.find((each) => notTakenMessageId(each.id) === messageId);

        return question === undefined ? null : (
          <div className="follow-up-actions" data-test-id="follow-up-not-taken">
            <p>{FOLLOW_UP_NOT_SENT}</p>
            <button
              type="button"
              data-test-id="follow-up-send-again"
              disabled={sending !== null || answering}
              onClick={() => send(question.question, question)}
            >
              {FOLLOW_UP_SEND_AGAIN_LABEL}
            </button>
          </div>
        );
      }}
      renderFollowUp={(followUp) => (
        <FollowUpAnswer
          followUp={followUp}
          cardText={cardText}
          cards={cards === undefined ? NO_CARDS : cards}
          busy={busyIds.has(followUp.id)}
          waitedOut={follow.waitedOut(followUp)}
          answeringElsewhere={answering && followUp.state !== 'answering'}
          actionError={actionErrors[followUp.id] ?? null}
          picking={pickingFor === followUp.id}
          focusOutcome={finalizedHere === followUp.id}
          onChoose={(kind, rqId) => choose(followUp, kind, rqId)}
          onRetry={() => retry(followUp)}
          onCheckAgain={() => checkAgain(followUp.id)}
          onCancelPick={() => {
            setPickingFor(null);
            // A read that failed is made again the next time a card is asked for.
            setCards((held) => (held === null ? undefined : held));
          }}
        />
      )}
    />
  );
};

const NO_CANCELLED: ReadonlySet<string> = new Set();

/** The message a question that was not taken is drawn as, apart from the follow-up it may become. */
const notTakenMessageId = (id: string): string => `${id}-not-taken`;
